import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Checkbox from '@material-ui/core/Checkbox';
import FormLabel from '@material-ui/core/FormLabel';
import Grid from '@material-ui/core/Grid';
import InputLabel from '@material-ui/core/InputLabel';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import Paper from '@material-ui/core/Paper';
import { makeStyles } from '@material-ui/core/styles';
import { rem } from 'polished';
import PropTypes from 'prop-types';
import React from 'react';

// The raw MUI components are used on purpose: the Playwright specs locate the move buttons by their
// aria-labels and the items by the `transfer-list-item-<value>-label` ids, and the AORedesign
// Button/Checkbox/ListItemText wrappers rewrite ids and defaults.
const useStyles = makeStyles((theme) => ({
  root: {
    justifyContent: 'start',
  },
  paper: {
    width: rem(275),
    height: rem(300),
    overflow: 'auto',
  },
  button: {
    margin: theme.spacing(0.5, 0),
  },
  topLabel: {
    fontSize: '0.85rem',
    lineHeight: rem('32px'),
  },
  label: {
    textAlign: 'center',
    fontSize: '0.75rem',
    margin: theme.spacing(1, 0),
  },
}));

const not = (a, b) => a.filter((value) => b.indexOf(value) === -1);

const intersection = (a, b) => a.filter((value) => b.indexOf(value) !== -1);

const TransferList = ({ label, left, leftLabel, right, rightLabel, checked, setLeft, setRight, setChecked, elevation = 1, variant = 'elevation' }) => {
  const classes = useStyles();
  const leftChecked = intersection(checked, left);
  const rightChecked = intersection(checked, right);

  const handleToggle = (value) => () => {
    const currentIndex = checked.indexOf(value);
    const newChecked = [...checked];

    if (currentIndex === -1) {
      newChecked.push(value);
    } else {
      newChecked.splice(currentIndex, 1);
    }

    setChecked(newChecked);
  };

  const handleAllRight = () => {
    setRight(right.concat(left));
    setLeft([]);
  };

  const handleCheckedRight = () => {
    setRight(right.concat(leftChecked));
    setLeft(not(left, leftChecked));
    setChecked(not(checked, leftChecked));
  };

  const handleCheckedLeft = () => {
    setLeft(left.concat(rightChecked));
    setRight(not(right, rightChecked));
    setChecked(not(checked, rightChecked));
  };

  const handleAllLeft = () => {
    setLeft(left.concat(right));
    setRight([]);
  };

  const customList = (items) => (
    <Paper className={classes.paper} elevation={elevation} variant={variant}>
      <List dense component="div" role="list">
        {items.map((value) => {
          const labelId = `transfer-list-item-${value}-label`;

          return (
            <ListItem key={value} role="listitem" button onClick={handleToggle(value)}>
              <ListItemIcon>
                <Checkbox checked={checked.indexOf(value) !== -1} tabIndex={-1} disableRipple inputProps={{ 'aria-labelledby': labelId }} />
              </ListItemIcon>
              <ListItemText id={labelId} primary={value} />
            </ListItem>
          );
        })}
        <ListItem />
      </List>
    </Paper>
  );

  return (
    <Box>
      <FormLabel className={classes.topLabel}>{label}</FormLabel>
      <Grid container spacing={2} justifyContent="center" alignItems="center" className={classes.root}>
        <Grid item>
          <InputLabel className={classes.label}>{leftLabel}</InputLabel>
          {customList(left)}
        </Grid>
        <Grid item>
          <Grid container direction="column" alignItems="center">
            <Button variant="outlined" size="small" className={classes.button} onClick={handleAllRight} disabled={left.length === 0} aria-label="move all right">
              ≫
            </Button>
            <Button variant="outlined" size="small" className={classes.button} onClick={handleCheckedRight} disabled={leftChecked.length === 0} aria-label="move selected right">
              &gt;
            </Button>
            <Button variant="outlined" size="small" className={classes.button} onClick={handleCheckedLeft} disabled={rightChecked.length === 0} aria-label="move selected left">
              &lt;
            </Button>
            <Button variant="outlined" size="small" className={classes.button} onClick={handleAllLeft} disabled={right.length === 0} aria-label="move all left">
              ≪
            </Button>
          </Grid>
        </Grid>
        <Grid item>
          <InputLabel className={classes.label}>{rightLabel}</InputLabel>
          {customList(right)}
        </Grid>
      </Grid>
    </Box>
  );
};

TransferList.propTypes = {
  label: PropTypes.string,
  leftLabel: PropTypes.string,
  left: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  setLeft: PropTypes.func,
  rightLabel: PropTypes.string,
  right: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  setRight: PropTypes.func,
  checked: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  setChecked: PropTypes.func,
  elevation: PropTypes.number,
  variant: PropTypes.oneOf(['outlined', 'elevation']),
};

export default TransferList;
